import React, { useRef } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import {
  Row,
  Paragraph,
  Ellipsis,
  LayoutBand,
  Avatar,
  Divider,
  useContainerWidth,
  type useInfoSheet,
  Tag,
  Cell,
} from '@fs/zion-ui'
import type { Person as PersonType } from '@fs/zion-tree-types'
import { Lifespan, Person, Pid } from '@fs/zion-person'
import { getNameInPreferredScript } from '@fs/zion-person-service'
import { usePreference } from '@fs/zion-preferences'
import { PersonQualityScoreButton } from '@fs/zion-person-quality-score'
import { NoticeProtected, TreePedigree } from '@fs/zion-icon'
import { useFeatureFlag } from '@fs/zion-flags'
import { isTranslationAvailable } from '@fs/zion-locale'
import { useGroupsContext, useShowWikiTree, WikiTreeAction } from '@fs/zion-tree-groups'
import StickyHeader from './StickyHeader'
import { useWikiTree } from '../../../providers/WikiTreeProvider'
import PersonPageTabs from '../PersonPageTabs'
import useSourceCentricDetailsFlag from '../../../data-service/useSourceCentricDetailsFlag'
import { CLASSIC_GUTTERS, SOURCE_CENTRIC_GUTTERS } from '../../constants'
import usePersonQualityScore from '../../../data-service/usePersonQualityScore'
import SearchRecordsMenuOverlay from '../person-banner/SearchRecordsMenuOverlay'
import ToolsMenuOverlay from '../person-banner/ToolsMenuOverlay'
import usePersonVisibility from '../../../hooks/usePersonVisibility'

const lifespanPidCss = css`
  margin-top: 3px;
`
/* Mirror the inline ActionChip's vertical metrics (20px box = 2px + 15px line + 3px padding) so the
 * lifespan/bullet text sits exactly like the chip label at every breakpoint — text-size-a is responsive,
 * so a natural line box drifts against the chip's fixed 20px height. Zion Paragraph drops className,
 * hence styling an inner span. */
const chipMetricsCss = css`
  display: block;
  line-height: 15px;
  padding: 2px 0 3px;
`

export const MINI_SEARCH_RECORDS_TESTID = 'mini-header-search-records-menu'
export const MINI_TOOLS_TESTID = 'mini-header-tools-menu'
/**
 * Namespaces the items *inside* the mini header's menus. Both menus are copies of the banner's, and
 * `MenuOverlay` keeps its children mounted (toggling only `display`), so without this every item id --
 * `tool-print`, `search-ancestry`, ... -- would match two elements and acceptance tests would resolve
 * the hidden mini header copy instead of the banner's.
 */
export const MINI_HEADER_TESTID_PREFIX = 'mini-header'

/** The WikiTree action, gated on the person actually being a WikiTree person. Renders nothing otherwise. */
function WikiTreeSummaryAction(): JSX.Element | null {
  const showWikiTree = useShowWikiTree()
  const wikiTreeOverlay = useWikiTree()

  if (!showWikiTree) return null
  return <WikiTreeAction overlay={wikiTreeOverlay} showLogo />
}

type MobilePersonSummaryProps = {
  focusPerson: PersonType
}

function MobilePersonSummary({ focusPerson }: MobilePersonSummaryProps): JSX.Element {
  const atSize = useContainerWidth()
  const componentSize = atSize({ default: 'sm', xxs: 'md', md: 'lg' })
  return (
    <>
      <Person personObj={focusPerson} doNotLinkToPersonPage componentSize={componentSize} avatarSize="smaller" />
      <WikiTreeSummaryAction />
    </>
  )
}

/** Subheader under the person name identifying the tree being viewed (family group tree or WikiTree). */
function TreeNameSubheader(): JSX.Element | null {
  const { currentGroup } = useGroupsContext()
  const showWikiTree = useShowWikiTree()

  if (showWikiTree) {
    return (
      <Row guttersY="none">
        <WikiTreeSummaryAction />
      </Row>
    )
  }

  if (currentGroup) {
    return (
      <Row alignItemsY="middle" gutters="pico">
        <TreePedigree size="xs" />
        <Paragraph secondary size="xs">
          <Ellipsis>{currentGroup.name}</Ellipsis>
        </Paragraph>
      </Row>
    )
  }

  return null
}

type DesktopPersonSummaryProps = {
  focusPerson: PersonType
  isSourceCentricOn: boolean
  scoreInfoSheet: ReturnType<typeof useInfoSheet>
}

function DesktopPersonSummary({
  focusPerson,
  isSourceCentricOn,
  scoreInfoSheet,
}: DesktopPersonSummaryProps): JSX.Element {
  const [t] = useTranslation()
  const showNameInRomanScriptPreference = usePreference('tree.showNamesInRomanScript')
  const showWikiTree = useShowWikiTree()
  const { isHidden } = usePersonVisibility()
  const hidePersonIsOn = useFeatureFlag('treeWeb_personR9_hidePerson').isOn
  const { score, loading: loadingPqs, showPersonQualityScore } = usePersonQualityScore()
  // SourceCentricBanner already tracks the PQS "mounted" analytics event whenever this duplicate
  // button renders, so start the ref at true to keep it from being counted twice per page view.
  const pqsHasTracked = useRef(true)

  const showActions = useContainerWidth()({ default: false, xl: true }) && isSourceCentricOn
  const showAsHidden = hidePersonIsOn && isHidden

  return (
    <Row alignX="end" alignItemsY="middle" gutters="xs" guttersY="nano">
      <Cell growX>
        <Row alignItemsY="middle" gutters="xs">
          <Avatar
            src={focusPerson.portraitUrl}
            sex={focusPerson.gender?.toLowerCase()}
            size={isSourceCentricOn ? 'md' : 'sm'}
            noShrinkX
          />
          <Cell growX>
            <Row alignItemsY="middle" gutters="xs" guttersY="nano">
              <Paragraph>
                <Ellipsis>
                  {focusPerson?.name
                    ? getNameInPreferredScript(focusPerson, Boolean(showNameInRomanScriptPreference)).fullName
                    : t('tree-person.unknown.label')}
                </Ellipsis>
              </Paragraph>

              <Row
                gutters="pico"
                alignItemsY="middle"
                css={lifespanPidCss}
                noShrinkX
                growX={!isSourceCentricOn && showWikiTree}
              >
                {focusPerson?.lifespan && (
                  <Paragraph noShrinkX secondary size="xs">
                    <span css={chipMetricsCss}>
                      <Lifespan personObj={focusPerson} />
                    </span>
                  </Paragraph>
                )}

                {focusPerson?.lifespan && (
                  <Paragraph noShrinkX secondary size="xs">
                    <span css={chipMetricsCss}>•</span>
                  </Paragraph>
                )}

                <Pid personObj={focusPerson} inline noShrinkX />
              </Row>

              {!isSourceCentricOn && <WikiTreeSummaryAction />}

              {/* WikiTree persons are always read-only/restricted; the WikiTree subheader covers that,
                  matching how PersonPage suppresses ReadOnlyBanner/ConfidentialBanner for WikiTree */}
              {/* Hiding a person clears this same flag, so exclude them here or the mini header
                  contradicts the Hidden banner by calling them confidential. */}
              {isSourceCentricOn &&
                !showWikiTree &&
                !focusPerson.visibleToAllWhenUsingFamilySearchApps &&
                !focusPerson.living &&
                !showAsHidden && (
                  <Tag text={t('tree-person.confidential.label')} color="yellow" Icon={NoticeProtected} noShrinkX />
                )}
              {isSourceCentricOn &&
                !showWikiTree &&
                showAsHidden &&
                isTranslationAvailable('tree-person.hidden.label') && (
                  <Tag text={t('tree-person.hidden.label')} color="gray" Icon={NoticeProtected} noShrinkX />
                )}
              {isSourceCentricOn && !showWikiTree && focusPerson.readOnly && (
                <Tag text={t('tree-person.read-only.label')} color="gray" Icon={NoticeProtected} noShrinkX />
              )}
            </Row>
            {isSourceCentricOn && <TreeNameSubheader />}
          </Cell>
        </Row>
      </Cell>
      {showActions && (
        <Row alignX="end" alignY="middle" gutters="xxs" guttersY="nano" noShrinkX>
          {showPersonQualityScore && (
            <PersonQualityScoreButton // eslint-disable-line test-selectors/button -- NA
              score={score}
              loading={loadingPqs}
              infoSheetOverlay={scoreInfoSheet}
              pqsHasTracked={pqsHasTracked}
              noShrinkX
            />
          )}
          {!focusPerson.deleted && (
            <SearchRecordsMenuOverlay
              noShrinkX
              data-testid={MINI_SEARCH_RECORDS_TESTID}
              testIdPrefix={MINI_HEADER_TESTID_PREFIX}
            />
          )}
          {!focusPerson.deleted && (
            <ToolsMenuOverlay noShrinkX data-testid={MINI_TOOLS_TESTID} testIdPrefix={MINI_HEADER_TESTID_PREFIX} />
          )}
        </Row>
      )}
    </Row>
  )
}

type MiniHeaderProps = {
  focusPerson: PersonType
  isMobile?: boolean
  showTemple: boolean
  scoreInfoSheet: ReturnType<typeof useInfoSheet>
}

export default function MiniHeader({
  focusPerson,
  isMobile = false,
  showTemple,
  scoreInfoSheet,
}: MiniHeaderProps): JSX.Element {
  const isSourceCentricOn = !!useSourceCentricDetailsFlag().data?.isOn
  return (
    <StickyHeader>
      <div aria-hidden="true">
        <LayoutBand maxWidth="lg" marginY="nano" marginX={isSourceCentricOn ? SOURCE_CENTRIC_GUTTERS : CLASSIC_GUTTERS}>
          {isMobile ? (
            <MobilePersonSummary focusPerson={focusPerson} />
          ) : (
            <DesktopPersonSummary
              focusPerson={focusPerson}
              isSourceCentricOn={isSourceCentricOn}
              scoreInfoSheet={scoreInfoSheet}
            />
          )}
        </LayoutBand>

        <Divider />

        <LayoutBand maxWidth="lg">
          <PersonPageTabs growX showTemple={showTemple} />
        </LayoutBand>
      </div>
    </StickyHeader>
  )
}
